/**
 * @typedef {{
 *     year: number,
 *     month: number,
 *     day: number,
 *     hour: number,
 *     minute: number,
 *     second: number,
 * }} ValueDateParts
 */

/**
 * @typedef {{
 *     color: null|string,
 *     intensive: null|string,
 *     hasColor: boolean,
 *     border: string,
 *     divide: string,
 *     bg: string,
 *     bg0: string,
 *     bg1: string,
 *     bg2: string,
 *     head: string,
 *     next: string|boolean,
 * }} ValueCssColor
 */

export class ValueService {
    /**
     * @param {unknown} value
     * @returns {boolean}
     */
    static isDefined(value) {
        return value !== null && value !== undefined;
    }

    /**
     * @param {unknown} value
     * @param {number} fallback
     * @returns {number}
     */
    static toInt(value, fallback = 0) {
        const result = parseInt(ValueService.toString(value), 10);
        return Number.isNaN(result) ? fallback : result;
    }

    /**
     * @param {unknown} value
     * @param {number} fallback
     * @returns {number}
     */
    static toUInt(value, fallback = 0) {
        const result = ValueService.toInt(value, fallback);
        return result < 0 ? 0 : result;
    }

    /**
     * @param {unknown} value
     * @param {number|null} fallback
     * @returns {number}
     */
    static toUIntOrNull(value, fallback = null) {
        const result = parseInt(ValueService.toString(value), 10);
        return Number.isNaN(result) ? fallback : result < 0 ? 0 : result;
    }

    /**
     * @param {unknown} value
     * @param {boolean} fallback
     * @returns {boolean}
     */
    static toBool(value, fallback = false) {
        if (!ValueService.isDefined(value)) {
            return fallback;
        }

        if (typeof value === 'boolean') {
            return value;
        }

        if (typeof value === 'number') {
            return value > 0;
        }

        const stringValue = ValueService.toString(value).toLowerCase();

        if (['true', '1', 'yes', 'on'].includes(stringValue)) {
            return true;
        }

        if (['false', '0', 'no', 'off', ''].includes(stringValue)) {
            return false;
        }

        return fallback;
    }

    /**
     * @param {unknown} value
     * @param {string} fallback
     * @returns {string}
     */
    static toString(value, fallback = '') {
        return ValueService.isDefined(value) ? String(value) : fallback;
    }

    /**
     * @param {unknown} value
     * @param {string} fallback
     * @returns {string}
     */
    static toStringSingleLine(value, fallback = '') {
        return ValueService.toString(value, fallback).replace(/\s+/g, ' ').trim();
    }

    /**
     * @param {unknown} value
     * @param {number} length
     * @returns {string}
     */
    static toPadded(value, length = 2) {
        return ValueService.toUInt(value).toString().padStart(length, '0');
    }

    /**
     * Zerlegt ISO-Angaben ("2026-07-24", "2026-07-24 15:30:00", "2026-07-24T15:30") und
     * deutsche Angaben ("24.07.2026", "24.07.2026 15:30"). Es wird bewusst kein Date-Objekt
     * zum Parsen benutzt, damit nichts über eine Zeitzone verschoben wird.
     *
     * @param {unknown} value
     * @returns {null|ValueDateParts}
     */
    static toDateParts(value) {
        const text = ValueService.toStringSingleLine(value).replace(',', '');

        const iso = text.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2}):(\d{1,2})(?::(\d{1,2}))?)?$/);
        if (iso !== null) {
            return ValueService._toDateParts(iso[1], iso[2], iso[3], iso[4], iso[5], iso[6]);
        }

        const german = text.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})(?:[ T](\d{1,2}):(\d{1,2})(?::(\d{1,2}))?)?$/);
        if (german !== null) {
            return ValueService._toDateParts(german[3], german[2], german[1], german[4], german[5], german[6]);
        }

        return null;
    }

    /**
     * Wert für <input type="date"> — identisch zum PHP-seitigen valueToDate().
     *
     * @param {unknown} value
     * @returns {string} '' oder 'YYYY-MM-DD'
     */
    static toDate(value) {
        const parts = ValueService.toDateParts(value);

        return parts === null ? '' : ValueService.toPadded(parts.year, 4) + '-' + ValueService.toPadded(parts.month) + '-' + ValueService.toPadded(parts.day);
    }

    /**
     * Wert für <input type="datetime-local"> — lokale Zeit, ohne Sekunden, ohne Zeitzone.
     *
     * @param {unknown} value
     * @returns {string} '' oder 'YYYY-MM-DDTHH:MM'
     */
    static toDateTimeLocal(value) {
        const parts = ValueService.toDateParts(value);

        return parts === null ? '' : ValueService.toDate(value) + 'T' + ValueService.toPadded(parts.hour) + ':' + ValueService.toPadded(parts.minute);
    }

    /**
     * @param {null|boolean|string} cssColor
     * @param {boolean} needColor
     * @return {ValueCssColor}
     */
    static splitCssColor(cssColor, needColor = false) {
        if (cssColor !== true && cssColor !== false) {
            cssColor = ValueService.toStringSingleLine(cssColor);
            if (cssColor === '') {
                cssColor = false;
            }
        }

        // -
        if (needColor && cssColor === false) {
            cssColor = true;
        }

        // -
        if (cssColor === true) {
            return this.#enrichColor({
                color: 'black',
                intensive: 'o50',
            });
        }

        if (ValueService.hasValue(cssColor)) {
            const parts = ValueService.toStringSingleLine(cssColor).split('-');

            return this.#enrichColor({
                color: parts[0],
                intensive: parts.length > 1 ? parts.slice(1).join('-') : null,
            });
        }

        return this.#enrichColor({
            color: null,
            intensive: null,
        });
    }

    /**
     * @param {{color: null|string, intensive: null|string}} cssColorSet
     * @return {ValueCssColor}
     */
    static #enrichColor(cssColorSet) {
        const useColor = cssColorSet.color ?? 'black';
        const useIntensive = cssColorSet.intensive ?? 'o100';
        return {
            ...cssColorSet,
            hasColor: cssColorSet.color !== null,
            border: useColor + '-o500',
            divide: 'divide-' + useColor + '-o500',
            bg: 'bg-' + useColor + '-' + useIntensive,
            bg0: 'bg-' + useColor + '-50',
            bg1: 'bg-' + useColor + '-100',
            bg2: 'bg-' + useColor + '-150',
            head: 'text-' + (useColor === 'black' || useColor === 'white' ? 'gray' : useColor) + '-700',
            next: cssColorSet.color !== null ? useColor + '-' + useIntensive : false,
        };
    }

    /**
     * @param {undefined|string} year
     * @param {undefined|string} month
     * @param {undefined|string} day
     * @param {undefined|string} hour
     * @param {undefined|string} minute
     * @param {undefined|string} second
     * @returns {null|ValueDateParts}
     */
    static _toDateParts(year, month, day, hour, minute, second) {
        const parts = {
            year: ValueService.toUInt(year),
            month: ValueService.toUInt(month),
            day: ValueService.toUInt(day),
            hour: ValueService.toUInt(hour),
            minute: ValueService.toUInt(minute),
            second: ValueService.toUInt(second),
        };

        if (parts.year < 1 || parts.month < 1 || parts.month > 12 || parts.day < 1) {
            return null;
        }
        if (parts.hour > 23 || parts.minute > 59 || parts.second > 59) {
            return null;
        }

        // Monatslänge inkl. Schaltjahr; über UTC, damit die Zeitzone nicht hineinspielt.
        const lastDay = new Date(Date.UTC(parts.year, parts.month, 0)).getUTCDate();

        return parts.day > lastDay ? null : parts;
    }

    /**
     * @param {unknown} value
     * @returns {boolean}
     */
    static hasValue(value) {
        return ValueService.isDefined(value) && ValueService.toStringSingleLine(value) !== '';
    }

    /**
     * @param {unknown} value
     * @param {number} size
     * @returns {string}
     */
    static doPadZero(value, size) {
        return String(value).padStart(size, '0');
    }

    /**
     * @return {string}
     */
    static getNowPrefix() {
        const now = new Date();
        const year = ValueService.doPadZero(now.getFullYear(), 4);
        const month = ValueService.doPadZero(now.getMonth() + 1, 2);
        const day = ValueService.doPadZero(now.getDate(), 2);
        const hour = ValueService.doPadZero(now.getHours(), 2);
        const minute = ValueService.doPadZero(now.getMinutes(), 2);
        return `${year}${month}${day}_${hour}${minute}`;
    }

    /**
     * Formatiert ein Datum nach einfachem PHP-ähnlichem Format.
     *
     * Unterstützt:
     * d = Tag zweistellig
     * m = Monat zweistellig
     * Y = Jahr vierstellig
     * H = Stunde zweistellig
     * i = Minute zweistellig
     * s = Sekunde zweistellig
     *
     * @param {unknown} value
     * @param {string} format
     * @returns {string}
     */
    static formatDate(value, format = 'd.m.Y') {
        const parts = ValueService.toDateParts(value);

        if (parts === null) {
            return '';
        }

        const replacements = {
            d: ValueService.toPadded(parts.day),
            m: ValueService.toPadded(parts.month),
            Y: ValueService.toPadded(parts.year, 4),
            H: ValueService.toPadded(parts.hour),
            i: ValueService.toPadded(parts.minute),
            s: ValueService.toPadded(parts.second),
        };

        return format.replace(/[dmYHis]/g, (key) => replacements[key]);
    }
}
